import { useState } from "react"
import { useMsal } from "@azure/msal-react"
import { IconChevronRight, IconTrash } from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { extractApiError } from "@/lib/api-errors"
import { cn } from "@/lib/utils"
import { useAppSelector } from "@/store"
import { hasPermission } from "@/store/permissionsSlice"
import type { BookingNote } from "../bookingCalendarApi"
import {
  MAX_GENERAL_NOTE_LENGTH,
  formatNoteTime,
  isDeletableNote,
  isInternalNote,
  mayDeleteNote,
  type NoteViewer,
  noteActionLabel,
  useAddBookingNoteMutation,
  useDeleteBookingNoteMutation,
  useGetBookingNotesQuery,
} from "../api/notesApi"

/**
 * The booking's notes, inside the appointment dialog: folded away until opened, then every note,
 * internal ones marked, oldest first. Edit opens it and lets staff add a note, or delete a general
 * internal one they wrote (anybody's, with appointments:manage). Renders nothing for a booking CMS has no row for — there is nowhere to keep a note
 * on one of those.
 *
 * Only ever shown to staff: the dialog is a staff page, and the endpoint behind it refuses anything
 * but a staff token.
 */
export function BookingNotesSection({ requestId }: { requestId?: string }) {
  if (!requestId) return null
  return <NotesForBooking key={requestId} requestId={requestId} />
}

function NotesForBooking({ requestId }: { requestId: string }) {
  const { data: notes, isLoading, isError } = useGetBookingNotesQuery(requestId)
  const [deleteNote] = useDeleteBookingNoteMutation()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const { instance, accounts } = useMsal()
  const { permissions } = useAppSelector(state => state.permissions)
  const viewer: NoteViewer = {
    upn: (instance.getActiveAccount() ?? accounts[0])?.username ?? null,
    canDeleteAny: hasPermission(permissions, "appointments:manage"),
  }

  function changeOpen(next: boolean) {
    setOpen(next)
    if (!next) setEditing(false)
  }

  function toggleEditing() {
    if (editing) {
      setEditing(false)
    } else {
      setEditing(true)
      setOpen(true)
    }
    setDeleteError(null)
  }

  async function remove(note: BookingNote) {
    if (deletingId) return
    setDeletingId(note.id)
    setDeleteError(null)

    const result = await deleteNote({ requestId, noteId: note.id })
    setDeletingId(null)
    if ("error" in result) {
      const failure = result.error as { status?: number | string; data?: unknown }
      setDeleteError(
        failure?.status === 403
          ? "You do not have permission to delete notes."
          : extractApiError(failure?.data) || "The note could not be deleted.",
      )
    }
  }

  return (
    <Collapsible open={open} onOpenChange={changeOpen} asChild>
      <section className="border-t pt-3" aria-labelledby="booking-notes-heading">
        <div className="flex items-center gap-2">
          <h3 id="booking-notes-heading" className="text-xs font-medium">
            <CollapsibleTrigger className="flex items-center gap-1">
              <IconChevronRight
                aria-hidden="true"
                className={cn("size-3.5 text-muted-foreground transition-transform", open && "rotate-90")}
              />
              Notes
              {notes && <span className="font-normal text-muted-foreground">({notes.length})</span>}
            </CollapsibleTrigger>
          </h3>
          <Button size="xs" variant="ghost" aria-pressed={editing} onClick={toggleEditing}>
            {editing ? "Done" : "Edit"}
          </Button>
        </div>

        <CollapsibleContent>
          {isLoading && <p className="mt-1.5 text-xs text-muted-foreground">Loading notes…</p>}
          {isError && <p role="alert" className="mt-1.5 text-xs text-red-700">The notes could not be loaded.</p>}
          {notes && (
            <BookingNotesList
              notes={notes}
              onDelete={editing ? remove : undefined}
              viewer={viewer}
              deletingId={deletingId}
            />
          )}
          {deleteError && <p role="alert" className="mt-1 text-xs text-red-700">{deleteError}</p>}

          {editing && <AddNoteForm requestId={requestId} />}
        </CollapsibleContent>
      </section>
    </Collapsible>
  )
}

/**
 * The list itself. Separate so it can be rendered on its own, and tested without a store. Given
 * `onDelete`, each note that may be deleted gets a delete button; the rest are records and stay.
 * Given a `viewer` too, only the notes that viewer may delete get one.
 */
export function BookingNotesList({ notes, onDelete, viewer, deletingId }: {
  notes: readonly BookingNote[]
  onDelete?: (note: BookingNote) => void
  viewer?: NoteViewer
  /** The note being deleted, whose button is disabled meanwhile. */
  deletingId?: string | null
}) {
  if (notes.length === 0) {
    return <p className="mt-1.5 text-xs text-muted-foreground">No notes yet.</p>
  }

  return (
    <ul className="mt-1.5 space-y-2">
      {notes.map(note => {
        const internal = isInternalNote(note)
        return (
          <li key={note.id} className="rounded-md border px-2.5 py-2 text-sm">
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              {internal ? (
                <Badge variant="secondary" title="Staff only. Never shown or sent to the customer.">Internal</Badge>
              ) : (
                <Badge variant="outline" title="Shown or sent to the customer.">Customer-visible</Badge>
              )}
              <span>{noteActionLabel(note.action)}</span>
              <span aria-hidden="true">·</span>
              <span>{note.createdBy}</span>
              <span aria-hidden="true">·</span>
              <time dateTime={note.createdAt}>{formatNoteTime(note.createdAt)}</time>
              {onDelete && (viewer ? mayDeleteNote(note, viewer) : isDeletableNote(note)) && (
                <Button
                  size="icon-xs"
                  variant="ghost"
                  className="ml-auto text-muted-foreground hover:text-red-700"
                  aria-label="Delete note"
                  title="Delete note"
                  disabled={deletingId != null}
                  onClick={() => onDelete(note)}
                >
                  <IconTrash />
                </Button>
              )}
            </div>
            <p className="mt-1 whitespace-pre-line break-words">{note.body}</p>
          </li>
        )
      })}
    </ul>
  )
}

function AddNoteForm({ requestId }: { requestId: string }) {
  const [addNote, addState] = useAddBookingNoteMutation()
  const [body, setBody] = useState("")
  const [error, setError] = useState<string | null>(null)

  const busy = addState.isLoading
  const empty = body.trim().length === 0

  async function submit() {
    if (empty || busy) return
    setError(null)

    const result = await addNote({ requestId, body: body.trim() })
    if ("error" in result) {
      const failure = result.error as { status?: number | string; data?: unknown }
      setError(
        failure?.status === 403
          ? "You do not have permission to add notes."
          : extractApiError(failure?.data) || "The note could not be saved.",
      )
      return
    }

    setBody("")
  }

  return (
    <div className="mt-3">
      <Label htmlFor={`booking-note-${requestId}`} className="text-xs font-medium">
        Add a note <span className="text-muted-foreground">(internal — staff only)</span>
      </Label>
      <Textarea
        id={`booking-note-${requestId}`}
        className="mt-1.5"
        rows={2}
        maxLength={MAX_GENERAL_NOTE_LENGTH}
        value={body}
        disabled={busy}
        placeholder="e.g. Called and left a message"
        onChange={event => setBody(event.target.value)}
      />
      {error && <p role="alert" className="mt-1 text-xs text-red-700">{error}</p>}
      <div className="mt-1.5 flex justify-end">
        <Button size="sm" variant="outline" disabled={busy || empty} onClick={submit}>
          {busy ? "Saving…" : "Add note"}
        </Button>
      </div>
    </div>
  )
}
